import { PipelineStatus } from "@prisma/client";
import { IconLink } from "@tabler/icons-react";
import { keepPreviousData } from "@tanstack/react-query";
import { useMemo } from "react";
import Markdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { OrgLink } from "~/components/OrgLink";
import { useAuthorization } from "~/contexts/authorizationProvider";
import { entityKindForHref } from "~/shared/ai/entity-link";
import { extractCustomerIdsFromAiMessage, formatAiMessageForDisplay } from "~/shared/ai/formatting";
import { isCustomerRouteUrl } from "~/shared/customers/customer-link-scope";
import { api } from "~/utils/api";
import type { EntityLinkKind } from "~/utils/types/assistant-report-artifact";

import { AssistantMarkdownTable } from "./AssistantMarkdownTable";
import { entityLinkAllowed } from "./entity-drill";

export const AssistantMessageBody = ({ children }: { children: string }) => {
  const { hasPermission } = useAuthorization();
  const customerIds = useMemo(() => extractCustomerIdsFromAiMessage(children), [children]);
  // Keeping the previous answer while a streamed message adds ids stops already-linked names from flickering;
  // an id the previous answer does not cover renders as the bold name until its own check returns.
  const scopeQuery = api.customers.getMany.useQuery(
    {
      ids: customerIds,
      includeArchived: true,
      pipelineStatuses: [PipelineStatus.ACTIVE, PipelineStatus.PROSPECT],
    },
    { enabled: customerIds.length > 0, placeholderData: keepPreviousData },
  );
  const customerIdsInViewScope = useMemo(
    () => (scopeQuery.data ? new Set(scopeQuery.data.map(customer => customer.id)) : null),
    [scopeQuery.data],
  );
  const kindAllowed = (kind: EntityLinkKind) => entityLinkAllowed(kind, hasPermission);
  const displayMessage = formatAiMessageForDisplay(children, {
    customerIdsInViewScope: customerIds.length === 0 ? null : (customerIdsInViewScope ?? new Set<string>()),
    entityLinkAllowed: kindAllowed,
  });

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={{
        table: AssistantMarkdownTable,
        a: ({ children: linkChildren, href }) => {
          const kind = href?.startsWith("/") ? entityKindForHref(href) : null;
          if (kind && !kindAllowed(kind)) {
            return <strong>{linkChildren}</strong>;
          }
          if (href?.startsWith("/") && isCustomerRouteUrl(href)) {
            const customerId = href.match(/\/customers\/([a-f0-9-]{36})/i)?.[1];
            if (customerId && (customerIds.length === 0 || !customerIdsInViewScope?.has(customerId))) {
              return <strong>{linkChildren}</strong>;
            }
          }
          return href?.startsWith("/") ? (
            <OrgLink href={href}>
              <strong>{linkChildren}</strong>
              <IconLink size={14} style={{ marginBottom: "-2px" }} />
            </OrgLink>
          ) : (
            <a href={href} target="_blank" rel="noreferrer">
              {linkChildren}
            </a>
          );
        },
      }}
    >
      {displayMessage}
    </Markdown>
  );
};
